import React, { useEffect, useRef, useState } from 'react';
import { ClickAwayListener, MenuList, Popper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StaticIcon } from 'web-components-ts';
import styled, { css } from 'styled-components';

const PRODUCT_MONITORING_COLOR = '#8A9196';
const IRIS_CLOUD_COLOR = '#7AAAC0';

const getIrisCloudUrl = () => {
  const configuredUrl = window.configuration?.irisCloudUrl;

  return (configuredUrl || window.location.origin).replace(/\/$/, '');
};

const formatTokenForPath = (token: string | null) => {
  if (!token) return '';

  return token.split('.').length === 3 ? token.replace(/\./g, '/') : token;
};

const buildIrisCloudUrl = () => {
  const accessToken = formatTokenForPath(localStorage.getItem('accessToken'));
  const refreshToken = formatTokenForPath(localStorage.getItem('refreshToken'));
  const baseUrl = getIrisCloudUrl();

  if (!accessToken || !refreshToken) return baseUrl;

  return `${baseUrl}/login/${accessToken}/${refreshToken}`;
};

const Container = styled.div`
  position: relative;
  height: 72px;
  display: flex;
  align-items: center;
  margin-right: 0.75rem;
`;

const LauncherButton = styled.button`
  width: 48px;
  height: 48px;
  border: 2px solid transparent;
  border-radius: 8px;
  color: #ffffff;
  background: transparent;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease;

  &:hover,
  &[aria-expanded='true'] {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Panel = styled.div`
  width: min(25rem, calc(100vw - 2rem));
  margin-top: 0.75rem;
  padding: 1rem;
  border-radius: 0 0 8px 8px;
  background: #ffffff;
  box-shadow: 0 14px 32px rgba(35, 37, 47, 0.16);
`;

const PanelTitle = styled.p`
  margin: 0 0 0;
  color: #8a9196;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0;
`;

const ProductsList = styled(MenuList)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 2fr));
  gap: 0.75rem;
  padding: 0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const productCardStyles = css`
  position: relative;
  min-height: 10rem;
  padding: 1.25rem;
  border: 1px solid #dddddd;
  border-radius: 8px;
  background: #ffffff;
  text-align: left;
`;

const ProductCard = styled.div`
  ${productCardStyles}
`;

const CurrentProduct = styled(ProductCard)`
  border-color: rgba(122, 170, 192, 0.55);
  background: rgba(226, 240, 237, 0.5);
  cursor: default;
`;

const ProductButton = styled.button`
  ${productCardStyles};
  width: 100%;
  font: inherit;
  cursor: pointer;
  transition: border-color 160ms ease, background-color 160ms ease;
  display: grid;

  &:hover {
    border-color: rgba(122, 170, 192, 0.75);
    background: #f7f7fa;
  }
`;

const ProductIcon = styled.span<{ color: string }>`
  width: 3rem;
  height: 3rem;
  border-radius: 8px;
  background: ${props => props.color};
  display: grid;
  place-items: center;
`;

const ProductHeader = styled.span`
  height: 3rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const ProductName = styled.span`
  display: block;
  color: #4c4d57;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.25;
`;

const ProductDescription = styled.span`
  display: block;
  margin-top: 0.35rem;
  color: #8a9196;
  font-size: 0.875rem;
  line-height: 1.25;
`;

const CurrentBadge = styled.span`
  border-radius: 999px;
  background: ${IRIS_CLOUD_COLOR};
  color: #ffffff;
  padding: 0.25rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 700;
`;

const ProductLauncher = () => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!open || !anchorRef.current) return undefined;

    return () => {
      anchorRef.current?.focus();
    };
  }, [open]);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }

    setOpen(false);
  };

  const handleOpenIrisCloud = () => {
    window.open(buildIrisCloudUrl(), '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  return (
    <Container>
      <LauncherButton
        ref={anchorRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('products.title')}
        onClick={handleToggle}
        data-test-id="product-launcher-button"
      >
        <StaticIcon name="ProductMenu" sx={{ fontSize: 20, color: '#FFFFFF' }} />
      </LauncherButton>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" disablePortal>
        <ClickAwayListener onClickAway={handleClose}>
          <Panel>
            <PanelTitle>{t('products.title')}</PanelTitle>
            <ProductsList autoFocusItem={open}>
              <CurrentProduct aria-current="page" data-test-id="product-launcher-production-monitoring-current">
                <ProductHeader>
                  <ProductIcon color={PRODUCT_MONITORING_COLOR}>
                    <StaticIcon name="ProductionMonitoring" sx={{ fontSize: 32, color: PRODUCT_MONITORING_COLOR }} />
                  </ProductIcon>
                  <CurrentBadge>{t('products.current')}</CurrentBadge>
                </ProductHeader>
                <ProductName>{t('products.production_monitoring.name')}</ProductName>
                <ProductDescription>{t('products.production_monitoring.description')}</ProductDescription>
              </CurrentProduct>

              <ProductButton
                type="button"
                onClick={handleOpenIrisCloud}
                data-test-id="product-launcher-iris-cloud-button"
              >
                <ProductHeader>
                  <ProductIcon color={IRIS_CLOUD_COLOR}>
                    <StaticIcon name="IrisCloud" sx={{ fontSize: 32, color: IRIS_CLOUD_COLOR }} />
                  </ProductIcon>
                </ProductHeader>
                <ProductName>{t('products.iris_cloud.name')}</ProductName>
                <ProductDescription>{t('products.iris_cloud.description')}</ProductDescription>
              </ProductButton>
            </ProductsList>
          </Panel>
        </ClickAwayListener>
      </Popper>
    </Container>
  );
};

export default ProductLauncher;
